import { isAcTerminalId } from "./ac";
import { clearAiEstimates } from "./ai/component-draft";
import type { ComponentDefinition, ComponentNode } from "./types";

function hasDcSupplyShape(def: ComponentDefinition): boolean {
  return def.id === "custom" && !def.requestedModel &&
    (def.renderVariant == null || def.renderVariant === "card") &&
    !def.acVoltage && !def.isFuseBlock && !def.fuseSlotType && !def.dcBreaker &&
    !def.internalDcConnections && !def.oneWayDcConnections && !def.currentShunt &&
    !def.settings && !def.bms && def.terminals.length === 4 &&
    new Set(def.terminals.map(t => t.id)).size === 4 &&
    def.terminals.every(t => t.id.length > 0 && !isAcTerminalId(t.id)) &&
    (["input", "output"] as const).every(direction =>
      (["positive", "negative"] as const).every(polarity =>
        def.terminals.filter(t => t.direction === direction && t.polarity === polarity).length === 1));
}

export function canConfigureDcSupply(def: ComponentDefinition): boolean {
  return hasDcSupplyShape(def) && def.category === "load" && !def.simulateStates &&
    def.ampOutput == null && def.outputVoltage == null;
}

export function isConfiguredDcSupply(def: ComponentDefinition): boolean {
  return hasDcSupplyShape(def) && def.category === "charger" &&
    def.inputVoltage != null && def.outputVoltage != null && def.ampOutput != null;
}

export type CustomDcSupplySettings = {
  inputVoltage: number;
  outputVoltage: number;
  outputVoltageRange?: { min: number; max: number };
  ampOutput: number;
  conversionEfficiency?: number;
};

/** Explicit setup supplies missing converter behavior; names and terminal
 * ratings never select a model or a voltage. No conductors are bonded. */
export function configureDcSupply(node: ComponentNode, settings: CustomDcSupplySettings): ComponentNode | null {
  const def = node.customDefinition;
  const range = settings.outputVoltageRange;
  if (node.definitionId !== "custom" || !def ||
    !(canConfigureDcSupply(def) || isConfiguredDcSupply(def)) ||
    [settings.inputVoltage, settings.outputVoltage, settings.ampOutput].some(v => !Number.isFinite(v) || v <= 0) ||
    (range != null && (!Number.isFinite(range.min) || !Number.isFinite(range.max) ||
      range.min <= 0 || range.max < range.min || settings.outputVoltage < range.min || settings.outputVoltage > range.max)) ||
    (settings.conversionEfficiency != null && (!Number.isFinite(settings.conversionEfficiency) ||
      settings.conversionEfficiency <= 0 || settings.conversionEfficiency > 1))) return null;
  const next: ComponentDefinition = {
    ...def, ...settings, category: "charger",
    simulateStates: { default: "on", options: [
      { id: "on", label: "On", ampMultiplier: 1, role: "output" },
      { id: "off", label: "Off", ampMultiplier: 0 },
    ] },
  };
  // The previous load draw is not an additional input demand. The solver
  // derives input power from the supplied output load and efficiency.
  const removed = ["ampDraw", "ampSurge", "watts", "voltage", "loadCurrentModel", "alwaysOn", "dutyHoursPerDay", "chargeAmpsTypical"] as const;
  for (const key of removed) delete next[key];
  if (settings.conversionEfficiency == null) delete next.conversionEfficiency;
  if (range == null) delete next.outputVoltageRange;
  const { dutyHoursPerDayOverride: _duty, alwaysOnOverride: _always, chargeCurrentLimitA: _limit, ...rest } = node;
  const keepLimit = isConfiguredDcSupply(def) && node.chargeCurrentLimitA != null &&
    Number.isFinite(node.chargeCurrentLimitA) && node.chargeCurrentLimitA >= 0;
  return { ...rest,
    ...(keepLimit ? { chargeCurrentLimitA: Math.min(node.chargeCurrentLimitA!, settings.ampOutput) } : {}),
    customDefinition: clearAiEstimates(next, ["category", "inputVoltage", "outputVoltage", "outputVoltageRange", "ampOutput", "conversionEfficiency", "simulateStates", ...removed]),
  };
}
